import { ComponentItem } from "designSystem/types/component-item-types";
import { ILocaleComponentResources, ILocaleProvider } from "designSystem/locale";
import locale from "csi/locale";
import sessionConstants from "csi/sessionConstants";
import { C8AttributeId, C8Node, C8NodeType } from "csi/typings";
import { isNullOrUndefined } from "csi/utility/coreUtility";

/**
 * Implement locale provider interface with C8 localization
 */

export function actionResource(nodeType: C8NodeType, actionId?: string): string {
	return locale.typeAction(nodeType, actionId);
}

export function attributeResource(nodeType: C8NodeType, id?: C8AttributeId): string {
	return locale.typeId(nodeType, id);
}

export function enumResource(enumValue: string, uppercase = false): string {
	return locale.getEnumName(enumValue, uppercase);
}

export function numberOfType(n: number, nodeType: string): string {
	return resource("ConjunctionNumberOfType", [n, type(nodeType, n !== 1)]);
}

export function getAttributeType(type: string): string {
	return locale.getAttributeType(type);
}

export function getDatePattern(): string {
	return sessionConstants.datePattern;
}

export function getErrorMessage(error: Error): string {
	return locale.getErrorMessage(error);
}

export function getTimePattern(): string {
	return sessionConstants.timePattern;
}

export function getNameOrUnnamed(name: string): string {
	return name || resource(resources.RES_UNNAMED);
}

export function getNodeName(node: C8Node): string {
	return locale.getNodeConfiguredName(node);
}

export function pathResource(nodeType: C8NodeType, path?: string): string {
	return locale.typePath(nodeType, path);
}

export function resource(res: string, resParams?: Array<number | string>): string {
	return res ? locale.parameterResource(res, resParams) : "";
}

export function type(nodeType: string, plural = false, lower = false): string {
	return plural ? locale.localeResolver.getTypePlural(nodeType, lower) : locale.localeResolver.getTypeName(nodeType, lower);
}

export function viewResource(nodeType: C8NodeType, viewId?: string): string {
	return locale.typeView(nodeType, viewId);
}

export function getComponentItemName(item: ComponentItem): string {
	const { id, name, node, res, resParams } = item;

	if (!isNullOrUndefined(name)) {
		return name;
	} else if (res) {
		return resource(res, resParams);
	} else if (node) {
		return getNodeName(node);
	}

	return id ?? "";
}

export function getComponentItemSlimName(item: ComponentItem): string {
	const name = getComponentItemName(item);
	const { name2 } = item;
	return name2 ? `${name} | ${name2}` : name;
}

const resources: ILocaleComponentResources = {
	RES_NEW_OF_TYPE: "New_Name",
	RES_UNNAMED: "NAME_EMPTY",
};

const localeProvider: ILocaleProvider = {
	resources,
	actionResource,
	attributeResource,
	enumResource,
	getAttributeType,
	getComponentItemName,
	getComponentItemSlimName,
	getDatePattern,
	getErrorMessage,
	getTimePattern,
	getNameOrUnnamed,
	getNodeName,
	numberOfType,
	pathResource,
	resource,
	type,
	viewResource,
};

export default localeProvider;